import { FC, useEffect, useRef, useCallback } from 'react';
import { ChatMessage } from '../../AIChat/types';
import ChatMessageBubble from './ChatMessageBubble';

interface ChatMessageListProps {
  messages: ChatMessage[];
}

const ChatMessageList: FC<ChatMessageListProps> = ({ messages }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastAssistantIdRef = useRef<string | null>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const setMessageRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) {
      messageRefs.current.set(id, el);
    } else {
      messageRefs.current.delete(id);
    }
  }, []);

  // Filter out tool role messages
  const visibleMessages = messages.filter((msg) => msg.role !== 'tool');

  useEffect(() => {
    const lastAssistant = [...visibleMessages].reverse().find((m) => m.role === 'assistant');
    if (!lastAssistant) return;

    // New assistant response started — scroll its top to the top of the container
    if (lastAssistant.id !== lastAssistantIdRef.current) {
      lastAssistantIdRef.current = lastAssistant.id;
      const el = messageRefs.current.get(lastAssistant.id);
      if (el && scrollRef.current) {
        el.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  }, [visibleMessages]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6">
      <div className="max-w-3xl mx-auto">
        {visibleMessages.map((message) => (
          <div key={message.id} ref={(el) => setMessageRef(message.id, el)}>
            <ChatMessageBubble message={message} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChatMessageList;
